import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, Stack, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, LinearProgress, CircularProgress, TextField, Button, Alert,
  Paper, Tooltip, Collapse, IconButton, Slider, Tabs, Tab, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  TrendingUp, TrendingDown, AccountBalance, Payments, Agriculture,
  Warning, CheckCircle, KeyboardArrowDown, KeyboardArrowUp, AttachMoney,
  Landscape, Spa, Build, Inventory2, Groups, Insights, AutoAwesome, Refresh,
  Save, Delete, Bookmark,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { reportService, dashboardService, aiAdvisorService, projectService, farmOperationService, seasonService, scenarioService } from '@smartgrow/shared';
import type {
  FinancialReport, ProjectFinancialSummary, ProjectAnalyticsReport,
  FarmFinancialSummaryReport, CropPerformanceReport, SeasonComparisonReport,
  WarningsReport, LaborCostReport, Scenario,
} from '@smartgrow/shared';

const fmt = (n: number) => `₦${n.toLocaleString('en-NG')}`;
const fmtPct = (n: number) => `${n.toFixed(1)}%`;

// Validated categorical palette (dataviz skill — passes CVD/contrast checks on white surface)
const CHART_COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];

const today = new Date();
const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
const pad = (n: number) => String(n).padStart(2, '0');
const toIso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

interface MetricCardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  color: 'success' | 'error' | 'primary' | 'warning' | 'info';
}

const MetricCard = ({ title, value, subtitle, icon, color }: MetricCardProps) => (
  <Card>
    <CardContent>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: `${color}.light`, color: `${color}.dark`, display: 'flex', flexShrink: 0 }}>
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" noWrap>{title}</Typography>
          <Typography variant="h6" fontWeight="bold" noWrap>{value}</Typography>
          {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const UtilizationBar = ({ pct, overBudget }: { pct: number; overBudget: boolean }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Box sx={{ flex: 1 }}>
      <LinearProgress
        variant="determinate"
        value={Math.min(pct, 100)}
        color={overBudget ? 'error' : pct > 80 ? 'warning' : 'success'}
        sx={{ height: 8, borderRadius: 4 }}
      />
    </Box>
    <Typography variant="caption" sx={{ minWidth: 42, textAlign: 'right', color: overBudget ? 'error.main' : 'text.secondary' }}>
      {fmtPct(pct)}
    </Typography>
  </Box>
);

const ProjectAnalyticsPanel = ({ projectId, startDate, endDate }: { projectId: string; startDate?: string; endDate?: string }) => {
  const { data, isLoading, isError } = useQuery<ProjectAnalyticsReport>({
    queryKey: ['project-analytics', projectId, startDate, endDate],
    queryFn: () => reportService.getProjectAnalytics(projectId, startDate, endDate),
  });

  if (isLoading) return <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></Box>;
  if (isError || !data) return <Alert severity="error" sx={{ m: 2 }}>Failed to load project analytics</Alert>;

  const paymentBreakdown = [
    { label: 'Salary', value: data.salaryPayments },
    { label: 'Contractor', value: data.contractorPayments },
    { label: 'Expenses', value: data.expensePayments },
    { label: 'Procurement', value: data.procurementPayments },
    { label: 'Other', value: data.otherPayments },
  ].filter(x => x.value > 0);

  const rangeLabel = startDate && endDate
    ? `${startDate} – ${endDate}`
    : startDate ? `From ${startDate}` : endDate ? `To ${endDate}` : 'Project lifetime';

  return (
    <Box sx={{ bgcolor: 'action.hover', p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Period: {rangeLabel}
      </Typography>
      <Grid container spacing={2}>
        {/* Budget breakdown */}
        <Grid item xs={12} sm={6} md={3}>
          <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold">BUDGET</Typography>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Total Budget</Typography>
              <Typography variant="body2" fontWeight="bold">{fmt(data.budget)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Disbursed</Typography>
              <Typography variant="body2" fontWeight="bold" color="error.main">{fmt(data.totalDisbursed)}</Typography>
            </Stack>
            {data.pendingApprovedExpenses > 0 && (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Pending Payout</Typography>
                <Typography variant="body2" fontWeight="bold" color="warning.main">{fmt(data.pendingApprovedExpenses)}</Typography>
              </Stack>
            )}
            <Divider />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Available</Typography>
              <Typography variant="body2" fontWeight="bold" color={data.budgetRemaining < 0 ? 'error.main' : 'success.main'}>
                {data.budgetRemaining < 0 ? `-${fmt(Math.abs(data.budgetRemaining))}` : fmt(data.budgetRemaining)}
              </Typography>
            </Stack>
          </Stack>
        </Grid>

        {/* Payment type breakdown */}
        <Grid item xs={12} sm={6} md={3}>
          <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold">PAYMENT BREAKDOWN</Typography>
            {paymentBreakdown.length === 0
              ? <Typography variant="body2" color="text.secondary">No payments yet</Typography>
              : paymentBreakdown.map(({ label, value }) => (
                <Stack key={label} direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">{label}</Typography>
                  <Typography variant="body2" fontWeight="medium">{fmt(value)}</Typography>
                </Stack>
              ))
            }
          </Stack>
        </Grid>

        {/* Operations */}
        <Grid item xs={12} sm={6} md={3}>
          <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold">OPERATIONS</Typography>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Total</Typography>
              <Typography variant="body2" fontWeight="bold">{data.totalOperations}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Active</Typography>
              <Typography variant="body2" fontWeight="bold" color="success.main">{data.inProgressOperations}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Completed</Typography>
              <Typography variant="body2" fontWeight="bold">{data.completedOperations}</Typography>
            </Stack>
            <Divider />
            <Typography variant="caption" color="text.secondary" fontWeight="bold">EXPENSES</Typography>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Pending</Typography>
              <Chip label={data.pendingExpenseCount} size="small" color="warning" variant="outlined" />
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Approved</Typography>
              <Chip label={data.approvedExpenseCount} size="small" color="success" variant="outlined" />
            </Stack>
          </Stack>
        </Grid>

        {/* Contracts */}
        <Grid item xs={12} sm={6} md={3}>
          <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold">CONTRACTS</Typography>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Total Value</Typography>
              <Typography variant="body2" fontWeight="bold">{fmt(data.totalContractValue)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Paid</Typography>
              <Typography variant="body2" fontWeight="bold" color="success.main">{fmt(data.totalContractPaid)}</Typography>
            </Stack>
            {data.totalContractValue > 0 && (
              <Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min((data.totalContractPaid / data.totalContractValue) * 100, 100)}
                  color="success"
                  sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
                />
              </Box>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

const FarmFinancialSummarySection = ({ navigate, projectId, operationId, projectBudgets }: { navigate: (path: string) => void; projectId?: string; operationId?: string; projectBudgets: Map<string, number> }) => {
  const { data, isLoading, isError } = useQuery<FarmFinancialSummaryReport>({
    queryKey: ['farm-financial-summary', projectId, operationId],
    queryFn: () => reportService.getFarmFinancialSummary({ projectId, operationId }),
  });

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>;
  if (isError || !data) return <Alert severity="error">Failed to load farm financial summary.</Alert>;

  const categoryPieData = data.costByCategory.map((c) => ({ name: c.categoryName, value: c.amountSpent }));
  const budgetFor = (pid: string) => projectBudgets.get(pid);
  const totalBudget = data.projectCosts.reduce((s, row) => s + (budgetFor(row.projectId) ?? 0), 0);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} lg={7}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Estimated Yield &amp; Revenue by Crop</Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell>Crop / Project</TableCell>
                <TableCell align="right">Area (acres)</TableCell>
                <TableCell align="right">Est. Yield</TableCell>
                <TableCell align="right">Unit Price</TableCell>
                <TableCell align="right">Est. Gross Revenue</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.cropRevenue.length === 0 ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>No crop projections entered yet</TableCell></TableRow>
              ) : data.cropRevenue.map((row, i) => (
                <TableRow key={`${row.cropVariety}-${i}`} hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/dashboard/farm/${row.farmOperationId}`)}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">{row.cropVariety}</Typography>
                    <Typography variant="caption" color="text.secondary"
                      sx={{ '&:hover': { textDecoration: 'underline' } }}
                      onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/projects/${row.projectId}`); }}
                    >
                      {row.projectName}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{row.areaAcres ?? '—'}</TableCell>
                  <TableCell align="right">{row.estimatedYieldQuantity ? `${row.estimatedYieldQuantity} ${row.yieldUnit ?? ''}` : '—'}</TableCell>
                  <TableCell align="right">{row.estimatedPricePerUnit ? fmt(row.estimatedPricePerUnit) : '—'}</TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight="bold">{row.estimatedGrossRevenue ? fmt(row.estimatedGrossRevenue) : '—'}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
            {data.cropRevenue.length > 0 && (
              <TableBody>
                <TableRow sx={{ bgcolor: 'action.selected' }}>
                  <TableCell colSpan={4}><Typography variant="body2" fontWeight="bold">Estimated Total Gross Revenue</Typography></TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight="bold" color="success.main">{fmt(data.estimatedGrossRevenue)}</Typography></TableCell>
                </TableRow>
              </TableBody>
            )}
          </Table>
        </TableContainer>

        <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3, mb: 1 }}>Cultivation Expenditure by Project</Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell>Project</TableCell>
                <TableCell align="right">Total Budget</TableCell>
                <TableCell align="right">Amount Spent</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.projectCosts.map((row) => {
                const budget = budgetFor(row.projectId);
                return (
                  <TableRow key={row.projectName} hover sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/dashboard/projects/${row.projectId}`)}
                  >
                    <TableCell>{row.projectName}</TableCell>
                    <TableCell align="right">{budget != null ? fmt(budget) : '—'}</TableCell>
                    <TableCell align="right">{fmt(row.amountSpent)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            <TableBody>
              <TableRow sx={{ bgcolor: 'action.selected' }}>
                <TableCell><Typography variant="body2" fontWeight="bold">Total Direct Cultivation Cost</Typography></TableCell>
                <TableCell align="right"><Typography variant="body2" fontWeight="bold">{fmt(totalBudget)}</Typography></TableCell>
                <TableCell align="right"><Typography variant="body2" fontWeight="bold">{fmt(data.totalDirectCultivationCost)}</Typography></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      <Grid item xs={12} lg={5}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Cost by Category</Typography>
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            {categoryPieData.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No approved expenses recorded yet</Typography>
            ) : (
              <Box sx={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={categoryPieData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                      {categoryPieData.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} stroke="#fff" strokeWidth={2} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(v: number) => fmt(v)} />
                    <Legend verticalAlign="bottom" height={36} wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            )}
          </CardContent>
        </Card>

        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Farm Financial Summary</Typography>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Total Budget</Typography>
              <Typography variant="body2" fontWeight="medium">{fmt(totalBudget)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Total Direct Cultivation Cost</Typography>
              <Typography variant="body2" fontWeight="medium">{fmt(data.totalDirectCultivationCost)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Staff Management (excl. salaries)</Typography>
              <Typography variant="body2" fontWeight="medium">{fmt(data.staffManagementCost)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Salaries</Typography>
              <Typography variant="body2" fontWeight="medium">{fmt(data.salaryCost)}</Typography>
            </Stack>
            <Divider />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" fontWeight="bold">Total Expenditure</Typography>
              <Typography variant="body2" fontWeight="bold" color="error.main">{fmt(data.totalExpenditure)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" fontWeight="bold">Estimated Gross Revenue</Typography>
              <Typography variant="body2" fontWeight="bold" color="success.main">{fmt(data.estimatedGrossRevenue)}</Typography>
            </Stack>
            <Divider />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body1" fontWeight="bold">Estimated Net Profit</Typography>
              <Typography variant="body1" fontWeight="bold" color={data.netProfit >= 0 ? 'success.main' : 'error.main'}>{fmt(data.netProfit)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body1" fontWeight="bold">ROI</Typography>
              <Typography variant="body1" fontWeight="bold" color={data.roiPercent >= 0 ? 'success.main' : 'error.main'}>{fmtPct(data.roiPercent)}</Typography>
            </Stack>
          </Stack>
        </Paper>
      </Grid>
    </Grid>
  );
};

const CropPerformanceSection = ({ projectId, operationId, seasonId }: { projectId?: string; operationId?: string; seasonId?: string }) => {
  const { data, isLoading, isError } = useQuery<CropPerformanceReport>({
    queryKey: ['crop-performance', projectId, operationId, seasonId],
    queryFn: () => reportService.getCropPerformance({ projectId, operationId, seasonId }),
  });
  const [selectedCrops, setSelectedCrops] = useState<string[]>([]);

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>;
  if (isError || !data) return <Alert severity="error">Failed to load crop performance.</Alert>;

  if (data.crops.length === 0) {
    return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No crop operations with variety data yet.</Typography>;
  }

  const rows = selectedCrops.length === 0 ? data.crops : data.crops.filter((r) => selectedCrops.includes(r.cropVariety));

  const totals = rows.reduce((acc, row) => ({
    operationCount: acc.operationCount + row.operationCount,
    totalAreaAcres: acc.totalAreaAcres + (row.totalAreaAcres ?? 0),
    expectedYieldKg: acc.expectedYieldKg + row.expectedYieldKg,
    actualYieldKg: acc.actualYieldKg + row.actualYieldKg,
    yieldVarianceKg: acc.yieldVarianceKg + row.yieldVarianceKg,
    totalCost: acc.totalCost + row.totalCost,
    profit: acc.profit + row.profit,
  }), { operationCount: 0, totalAreaAcres: 0, expectedYieldKg: 0, actualYieldKg: 0, yieldVarianceKg: 0, totalCost: 0, profit: 0 });
  const totalsRoiPercent = totals.totalCost > 0 ? (totals.profit / totals.totalCost) * 100 : 0;
  const anyProjected = rows.some((r) => r.projected);

  return (
    <Box>
      <TextField
        select fullWidth size="small" label="Filter by crop"
        SelectProps={{
          multiple: true,
          value: selectedCrops,
          onChange: (e) => setSelectedCrops(e.target.value as unknown as string[]),
          renderValue: (selected) => (selected as string[]).length === 0 ? 'All crops' : (selected as string[]).join(', '),
        }}
        sx={{ mb: 2, maxWidth: 400 }}
      >
        {data.crops.map((c) => (
          <MenuItem key={c.cropVariety} value={c.cropVariety}>{c.cropVariety}</MenuItem>
        ))}
      </TextField>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell>Crop</TableCell>
              <TableCell align="right">Ops</TableCell>
              <TableCell align="right">Area (acres)</TableCell>
              <TableCell align="right">Expected Yield (kg)</TableCell>
              <TableCell align="right">Yield (kg)</TableCell>
              <TableCell align="right">Yield Variance</TableCell>
              <TableCell align="right">Cost</TableCell>
              <TableCell align="right">Profit</TableCell>
              <TableCell align="right">ROI</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.cropVariety} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">{row.cropVariety}</Typography>
                  {row.projected && (
                    <Chip label="Projected" size="small" color="info" variant="outlined" sx={{ mt: 0.5 }} />
                  )}
                </TableCell>
                <TableCell align="right">{row.operationCount}</TableCell>
                <TableCell align="right">{row.totalAreaAcres ?? '—'}</TableCell>
                <TableCell align="right">{row.expectedYieldKg.toLocaleString()}</TableCell>
                <TableCell align="right">{(row.projected ? row.expectedYieldKg : row.actualYieldKg).toLocaleString()}</TableCell>
                <TableCell align="right">
                  {row.projected ? (
                    <Typography variant="body2" color="text.secondary">— no harvest yet</Typography>
                  ) : (
                    <Typography variant="body2" color={row.yieldVarianceKg >= 0 ? 'success.main' : 'error.main'}>
                      {row.yieldVarianceKg >= 0 ? '+' : ''}{row.yieldVarianceKg.toLocaleString()} kg ({fmtPct(row.yieldVariancePercent)})
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">{fmt(row.totalCost)}</TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="bold" color={row.profit >= 0 ? 'success.main' : 'error.main'}>{fmt(row.profit)}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Chip label={fmtPct(row.roiPercent)} size="small" color={row.roiPercent >= 0 ? 'success' : 'error'} variant="outlined" />
                </TableCell>
              </TableRow>
            ))}
            {rows.length > 1 && (
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell><Typography variant="body2" fontWeight="bold">Total{anyProjected && ' (mixed actual/projected)'}</Typography></TableCell>
                <TableCell align="right"><Typography variant="body2" fontWeight="bold">{totals.operationCount}</Typography></TableCell>
                <TableCell align="right"><Typography variant="body2" fontWeight="bold">{totals.totalAreaAcres.toLocaleString()}</Typography></TableCell>
                <TableCell align="right"><Typography variant="body2" fontWeight="bold">{totals.expectedYieldKg.toLocaleString()}</Typography></TableCell>
                <TableCell align="right"><Typography variant="body2" fontWeight="bold">{totals.actualYieldKg.toLocaleString()}</Typography></TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="bold" color={totals.yieldVarianceKg >= 0 ? 'success.main' : 'error.main'}>
                    {totals.yieldVarianceKg >= 0 ? '+' : ''}{totals.yieldVarianceKg.toLocaleString()} kg
                  </Typography>
                </TableCell>
                <TableCell align="right"><Typography variant="body2" fontWeight="bold">{fmt(totals.totalCost)}</Typography></TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="bold" color={totals.profit >= 0 ? 'success.main' : 'error.main'}>{fmt(totals.profit)}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Chip label={fmtPct(totalsRoiPercent)} size="small" color={totalsRoiPercent >= 0 ? 'success' : 'error'} variant="outlined" />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const SeasonComparisonSection = ({ projectId, operationId }: { projectId?: string; operationId?: string }) => {
  const { data, isLoading, isError } = useQuery<SeasonComparisonReport>({
    queryKey: ['season-comparison', projectId, operationId],
    queryFn: () => reportService.getSeasonComparison({ projectId, operationId }),
  });

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>;
  if (isError || !data) return <Alert severity="error">Failed to load season comparison.</Alert>;

  if (data.seasons.length === 0) {
    return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No operations linked to a year-scoped season yet.</Typography>;
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'action.hover' }}>
            <TableCell>Season</TableCell>
            <TableCell align="right">Ops</TableCell>
            <TableCell align="right">Area (acres)</TableCell>
            <TableCell align="right">Expected Yield (kg)</TableCell>
            <TableCell align="right">Actual Yield (kg)</TableCell>
            <TableCell align="right">Cost</TableCell>
            <TableCell align="right">Profit</TableCell>
            <TableCell align="right">ROI</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.seasons.map((row) => (
            <TableRow key={`${row.year}-${row.seasonType}`} hover>
              <TableCell><Typography variant="body2" fontWeight="medium">{row.seasonName}</Typography></TableCell>
              <TableCell align="right">{row.operationCount}</TableCell>
              <TableCell align="right">{row.totalAreaAcres ?? '—'}</TableCell>
              <TableCell align="right">{row.expectedYieldKg.toLocaleString()}</TableCell>
              <TableCell align="right">{row.actualYieldKg.toLocaleString()}</TableCell>
              <TableCell align="right">{fmt(row.totalCost)}</TableCell>
              <TableCell align="right">
                <Typography variant="body2" fontWeight="bold" color={row.profit >= 0 ? 'success.main' : 'error.main'}>{fmt(row.profit)}</Typography>
              </TableCell>
              <TableCell align="right">
                <Chip label={fmtPct(row.roiPercent)} size="small" color={row.roiPercent >= 0 ? 'success' : 'error'} variant="outlined" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const WarningsSection = ({ navigate }: { navigate: (path: string) => void }) => {
  const { data, isLoading, isError } = useQuery<WarningsReport>({
    queryKey: ['warnings'],
    queryFn: () => reportService.getWarnings(),
  });

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>;
  if (isError || !data) return <Alert severity="error">Failed to load warnings.</Alert>;

  const noWarnings = data.lowStockInputs.length === 0 && data.maintenanceDueEquipment.length === 0;
  if (noWarnings) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
        <CheckCircle color="success" fontSize="small" />
        <Typography variant="body2" color="text.secondary">No low-stock inputs or upcoming equipment maintenance.</Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      {data.lowStockInputs.length > 0 && (
        <Grid item xs={12} md={6}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Inventory2 fontSize="small" color="warning" />
            <Typography variant="subtitle2" fontWeight="bold">Low Stock ({data.lowStockInputs.length})</Typography>
          </Stack>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell>Input</TableCell>
                  <TableCell align="right">In Stock</TableCell>
                  <TableCell align="right">Reorder Level</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.lowStockInputs.map((row) => (
                  <TableRow key={row.id} hover sx={{ cursor: 'pointer' }}
                    onClick={() => navigate('/dashboard/inventory')}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">{row.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{row.inputType}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="warning.main">{row.quantityInStock} {row.unit ?? ''}</Typography>
                    </TableCell>
                    <TableCell align="right">{row.reorderLevel ?? '—'} {row.unit ?? ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      )}
      {data.maintenanceDueEquipment.length > 0 && (
        <Grid item xs={12} md={6}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Build fontSize="small" color="warning" />
            <Typography variant="subtitle2" fontWeight="bold">Maintenance Due ({data.maintenanceDueEquipment.length})</Typography>
          </Stack>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell>Equipment</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.maintenanceDueEquipment.map((row) => (
                  <TableRow key={row.id} hover sx={{ cursor: 'pointer' }}
                    onClick={() => navigate('/dashboard/inventory')}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">{row.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{row.equipmentType}</Typography>
                    </TableCell>
                    <TableCell>{row.nextMaintenanceDate ?? '—'}</TableCell>
                    <TableCell><Chip label={row.status} size="small" variant="outlined" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      )}
    </Grid>
  );
};

const LaborCostSection = ({ navigate, projectId }: { navigate: (path: string) => void; projectId?: string }) => {
  const { data, isLoading, isError } = useQuery<LaborCostReport>({
    queryKey: ['labor-cost', projectId],
    queryFn: () => reportService.getLaborCost({ projectId }),
  });

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>;
  if (isError || !data) return <Alert severity="error">Failed to load labor cost report.</Alert>;

  if (data.projects.length === 0) {
    return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No completed tasks with recorded cost yet.</Typography>;
  }

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell>Project</TableCell>
              <TableCell align="right">Staff</TableCell>
              <TableCell align="right">Contractor</TableCell>
              <TableCell align="right">External</TableCell>
              <TableCell align="right">Unassigned</TableCell>
              <TableCell align="right">Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.projects.map((row) => (
              <TableRow key={row.projectId} hover sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/dashboard/projects/${row.projectId}`)}
              >
                <TableCell><Typography variant="body2" fontWeight="medium">{row.projectName}</Typography></TableCell>
                <TableCell align="right">{fmt(row.staffCost)}</TableCell>
                <TableCell align="right">{fmt(row.contractorCost)}</TableCell>
                <TableCell align="right">{fmt(row.externalCost)}</TableCell>
                <TableCell align="right">{fmt(row.unassignedCost)}</TableCell>
                <TableCell align="right"><Typography variant="body2" fontWeight="bold">{fmt(row.totalCost)}</Typography></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {projectId && data.operations && data.operations.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>By Operation</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell>Operation</TableCell>
                  <TableCell align="right">Staff</TableCell>
                  <TableCell align="right">Contractor</TableCell>
                  <TableCell align="right">External</TableCell>
                  <TableCell align="right">Unassigned</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.operations.map((row) => (
                  <TableRow key={row.operationId} hover sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/dashboard/farm/${row.operationId}`)}
                  >
                    <TableCell><Typography variant="body2" fontWeight="medium">{row.operationName}</Typography></TableCell>
                    <TableCell align="right">{fmt(row.staffCost)}</TableCell>
                    <TableCell align="right">{fmt(row.contractorCost)}</TableCell>
                    <TableCell align="right">{fmt(row.externalCost)}</TableCell>
                    <TableCell align="right">{fmt(row.unassignedCost)}</TableCell>
                    <TableCell align="right"><Typography variant="body2" fontWeight="bold">{fmt(row.totalCost)}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

const ScenarioPlannerSection = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useQuery<FarmFinancialSummaryReport>({
    queryKey: ['farm-financial-summary'],
    queryFn: () => reportService.getFarmFinancialSummary(),
  });

  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [selectedOperationId, setSelectedOperationId] = useState<string>('');
  const [yieldDelta, setYieldDelta] = useState(0);
  const [priceDelta, setPriceDelta] = useState(0);
  const [inputCostDelta, setInputCostDelta] = useState(0);
  const [laborCostDelta, setLaborCostDelta] = useState(0);
  const [harvestLossPercent, setHarvestLossPercent] = useState(0);
  const [areaExpansionPercent, setAreaExpansionPercent] = useState(0);
  const [narrative, setNarrative] = useState<string | null>(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [scenarioName, setScenarioName] = useState('');

  const queryClient = useQueryClient();

  const narrativeMutation = useMutation({
    mutationFn: aiAdvisorService.getScenarioNarrative,
    onSuccess: (res) => setNarrative(res.narrative),
  });

  const savedScenariosQuery = useQuery<Scenario[]>({
    queryKey: ['scenarios'],
    queryFn: () => scenarioService.getAll(),
  });

  const saveMutation = useMutation({
    mutationFn: scenarioService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scenarios'] });
      setSaveDialogOpen(false);
      setScenarioName('');
    },
  });

  const deleteScenarioMutation = useMutation({
    mutationFn: scenarioService.delete,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['scenarios'] }),
  });

  const projectOptions = (data?.cropRevenue ?? []).reduce((acc, row) => {
    if (!acc.some((p) => p.id === row.projectId)) acc.push({ id: row.projectId, name: row.projectName });
    return acc;
  }, [] as { id: string; name: string }[]);

  // Default to a single project (per-project scope) rather than the farm-wide "All Projects"
  // baseline — "all" is an explicit user choice, not the default.
  useEffect(() => {
    if (selectedProjectId === '' && projectOptions.length > 0) {
      setSelectedProjectId(projectOptions[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectOptions.length]);

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>;
  if (isError || !data) return <Alert severity="error">Failed to load scenario baseline.</Alert>;

  const isAllProjects = selectedProjectId === '';

  const operationOptions = isAllProjects
    ? []
    : data.cropRevenue.filter((r) => r.projectId === selectedProjectId);

  const isOperationScoped = !isAllProjects && selectedOperationId !== '';

  // Net effective yield change combines the direct yield delta with post-harvest loss/wastage —
  // a real-world lever distinct from the yield itself (e.g. spoilage, poor storage, transport loss).
  const netYieldMult = (1 + yieldDelta / 100) * (1 - harvestLossPercent / 100) * (1 + areaExpansionPercent / 100);
  const priceMult = 1 + priceDelta / 100;
  // Input (fertilizer/seed/agrochemical) and labor costs move independently — area expansion raises
  // input cost roughly in proportion to the extra land, labor cost is held separate from that delta.
  const costMult = (1 + inputCostDelta / 100) * (1 + areaExpansionPercent / 100) + (laborCostDelta / 100);

  const baselineRevenue = isOperationScoped
    ? data.cropRevenue.find((r) => r.farmOperationId === selectedOperationId)?.estimatedGrossRevenue ?? 0
    : isAllProjects
      ? data.estimatedGrossRevenue
      : data.cropRevenue
          .filter((r) => r.projectId === selectedProjectId)
          .reduce((sum, r) => sum + (r.estimatedGrossRevenue ?? 0), 0);

  // Cost isn't tracked per-operation today (only per-project), so an operation-scoped baseline
  // takes that operation's share of its project's cost, proportional to its share of project revenue —
  // an approximation, flagged in the caption below.
  const projectCostBaseline = isAllProjects
    ? data.totalExpenditure
    : data.projectCosts.find((p) => p.projectId === selectedProjectId)?.amountSpent ?? 0;

  const baselineCost = isOperationScoped
    ? (() => {
        const projectRevenueTotal = data.cropRevenue
          .filter((r) => r.projectId === selectedProjectId)
          .reduce((sum, r) => sum + (r.estimatedGrossRevenue ?? 0), 0);
        const opRevenue = data.cropRevenue.find((r) => r.farmOperationId === selectedOperationId)?.estimatedGrossRevenue ?? 0;
        return projectRevenueTotal > 0 ? projectCostBaseline * (opRevenue / projectRevenueTotal) : 0;
      })()
    : projectCostBaseline;

  const baselineProfit = baselineRevenue - baselineCost;
  const baselineRoi = baselineCost > 0 ? (baselineProfit / baselineCost) * 100 : 0;

  const projectedRevenue = baselineRevenue * netYieldMult * priceMult;
  const projectedCost = baselineCost * costMult;
  const projectedProfit = projectedRevenue - projectedCost;
  const projectedRoi = projectedCost > 0 ? (projectedProfit / projectedCost) * 100 : 0;

  const isChanged = yieldDelta !== 0 || priceDelta !== 0 || inputCostDelta !== 0
    || laborCostDelta !== 0 || harvestLossPercent !== 0 || areaExpansionPercent !== 0;

  const handleReset = () => {
    setYieldDelta(0);
    setPriceDelta(0);
    setInputCostDelta(0);
    setLaborCostDelta(0);
    setHarvestLossPercent(0);
    setAreaExpansionPercent(0);
    setNarrative(null);
  };

  const scopeLabel = isOperationScoped
    ? `"${operationOptions.find((o) => o.farmOperationId === selectedOperationId)?.cropVariety ?? 'the selected operation'}" operation`
    : isAllProjects
      ? 'the farm-wide'
      : `"${projectOptions.find((p) => p.id === selectedProjectId)?.name ?? 'the selected project'}"`;

  const handleExplain = () => {
    narrativeMutation.mutate({
      scopeLabel: scopeLabel,
      yieldDeltaPercent: yieldDelta,
      priceDeltaPercent: priceDelta,
      costDeltaPercent: inputCostDelta,
      baselineRevenue,
      baselineCost,
      baselineProfit,
      baselineRoiPercent: baselineRoi,
      projectedRevenue,
      projectedCost,
      projectedProfit,
      projectedRoiPercent: projectedRoi,
    });
  };

  const handleSaveConfirm = () => {
    if (!scenarioName.trim()) return;
    saveMutation.mutate({
      name: scenarioName.trim(),
      projectId: isAllProjects ? undefined : selectedProjectId,
      projectName: isAllProjects ? undefined : projectOptions.find((p) => p.id === selectedProjectId)?.name,
      farmOperationId: isOperationScoped ? selectedOperationId : undefined,
      farmOperationName: isOperationScoped
        ? operationOptions.find((o) => o.farmOperationId === selectedOperationId)?.cropVariety
        : undefined,
      yieldDeltaPercent: yieldDelta,
      priceDeltaPercent: priceDelta,
      inputCostDeltaPercent: inputCostDelta,
      laborCostDeltaPercent: laborCostDelta,
      harvestLossPercent: harvestLossPercent,
      areaExpansionPercent: areaExpansionPercent,
      baselineRevenue,
      baselineCost,
      baselineProfit,
      baselineRoiPercent: baselineRoi,
      projectedRevenue,
      projectedCost,
      projectedProfit,
      projectedRoiPercent: projectedRoi,
      narrative: narrative ?? undefined,
    });
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={5}>
        <Stack spacing={2.5}>
          <TextField
            select fullWidth size="small" label="Project"
            value={selectedProjectId}
            onChange={(e) => { setSelectedProjectId(e.target.value); setSelectedOperationId(''); setNarrative(null); }}
          >
            <MenuItem value="">All Projects (farm-wide)</MenuItem>
            {projectOptions.map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            select fullWidth size="small" label="Operation (optional drill-down)"
            value={selectedOperationId}
            disabled={isAllProjects}
            onChange={(e) => { setSelectedOperationId(e.target.value); setNarrative(null); }}
          >
            <MenuItem value="">Whole project</MenuItem>
            {operationOptions.map((op) => (
              <MenuItem key={op.farmOperationId} value={op.farmOperationId}>{op.cropVariety}</MenuItem>
            ))}
          </TextField>
          <Box>
            <Typography variant="body2" fontWeight="medium" gutterBottom>Yield {yieldDelta > 0 ? '+' : ''}{yieldDelta}%</Typography>
            <Slider value={yieldDelta} onChange={(_, v) => setYieldDelta(v as number)}
              min={-50} max={50} step={5} marks={[{ value: 0, label: '0%' }]} valueLabelDisplay="auto" />
          </Box>
          <Box>
            <Typography variant="body2" fontWeight="medium" gutterBottom>Price per Unit {priceDelta > 0 ? '+' : ''}{priceDelta}%</Typography>
            <Slider value={priceDelta} onChange={(_, v) => setPriceDelta(v as number)}
              min={-50} max={50} step={5} marks={[{ value: 0, label: '0%' }]} valueLabelDisplay="auto" />
          </Box>
          <Box>
            <Typography variant="body2" fontWeight="medium" gutterBottom>Input/Fertilizer Cost {inputCostDelta > 0 ? '+' : ''}{inputCostDelta}%</Typography>
            <Slider value={inputCostDelta} onChange={(_, v) => setInputCostDelta(v as number)}
              min={-50} max={50} step={5} marks={[{ value: 0, label: '0%' }]} valueLabelDisplay="auto" />
          </Box>
          <Box>
            <Typography variant="body2" fontWeight="medium" gutterBottom>Labor Cost {laborCostDelta > 0 ? '+' : ''}{laborCostDelta}%</Typography>
            <Slider value={laborCostDelta} onChange={(_, v) => setLaborCostDelta(v as number)}
              min={-50} max={50} step={5} marks={[{ value: 0, label: '0%' }]} valueLabelDisplay="auto" />
          </Box>
          <Box>
            <Typography variant="body2" fontWeight="medium" gutterBottom>Post-Harvest Loss / Wastage {harvestLossPercent}%</Typography>
            <Slider value={harvestLossPercent} onChange={(_, v) => setHarvestLossPercent(v as number)}
              min={0} max={40} step={5} marks={[{ value: 0, label: '0%' }]} valueLabelDisplay="auto" />
          </Box>
          <Box>
            <Typography variant="body2" fontWeight="medium" gutterBottom>Area Expansion {areaExpansionPercent > 0 ? '+' : ''}{areaExpansionPercent}%</Typography>
            <Slider value={areaExpansionPercent} onChange={(_, v) => setAreaExpansionPercent(v as number)}
              min={0} max={100} step={10} marks={[{ value: 0, label: '0%' }]} valueLabelDisplay="auto" />
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button size="small" startIcon={<Refresh />} onClick={handleReset} disabled={!isChanged}>Reset</Button>
            <Button size="small" variant="outlined" startIcon={narrativeMutation.isPending ? <CircularProgress size={16} /> : <AutoAwesome />}
              onClick={handleExplain} disabled={!isChanged || narrativeMutation.isPending}>
              Explain this scenario
            </Button>
            <Button size="small" variant="outlined" startIcon={<Save />}
              onClick={() => setSaveDialogOpen(true)} disabled={!isChanged}>
              Save scenario
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Deltas apply against {scopeLabel} baseline (estimated revenue and direct cultivation cost).
            {isOperationScoped && ' Operation cost is an approximation — apportioned from the project total by revenue share, since cost is only tracked per-project today.'}
            {' '}Recompute here is client-side only — save a scenario below to keep a named snapshot for later comparison.
          </Typography>
        </Stack>
      </Grid>
      <Grid item xs={12} md={7}>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell>Metric</TableCell>
                <TableCell align="right">Baseline</TableCell>
                <TableCell align="right">Projected</TableCell>
                <TableCell align="right">Change</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Revenue</TableCell>
                <TableCell align="right">{fmt(baselineRevenue)}</TableCell>
                <TableCell align="right"><Typography variant="body2" fontWeight="bold">{fmt(projectedRevenue)}</Typography></TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color={projectedRevenue >= baselineRevenue ? 'success.main' : 'error.main'}>
                    {projectedRevenue >= baselineRevenue ? '+' : ''}{fmt(projectedRevenue - baselineRevenue)}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Cost</TableCell>
                <TableCell align="right">{fmt(baselineCost)}</TableCell>
                <TableCell align="right"><Typography variant="body2" fontWeight="bold">{fmt(projectedCost)}</Typography></TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color={projectedCost <= baselineCost ? 'success.main' : 'error.main'}>
                    {projectedCost >= baselineCost ? '+' : ''}{fmt(projectedCost - baselineCost)}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Net Profit</TableCell>
                <TableCell align="right">{fmt(baselineProfit)}</TableCell>
                <TableCell align="right"><Typography variant="body2" fontWeight="bold" color={projectedProfit >= 0 ? 'success.main' : 'error.main'}>{fmt(projectedProfit)}</Typography></TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color={projectedProfit >= baselineProfit ? 'success.main' : 'error.main'}>
                    {projectedProfit >= baselineProfit ? '+' : ''}{fmt(projectedProfit - baselineProfit)}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>ROI</TableCell>
                <TableCell align="right">{fmtPct(baselineRoi)}</TableCell>
                <TableCell align="right">
                  <Chip label={fmtPct(projectedRoi)} size="small" color={projectedRoi >= 0 ? 'success' : 'error'} variant="outlined" />
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color={projectedRoi >= baselineRoi ? 'success.main' : 'error.main'}>
                    {projectedRoi >= baselineRoi ? '+' : ''}{fmtPct(projectedRoi - baselineRoi)}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {narrativeMutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>Failed to generate explanation. Please try again.</Alert>
        )}
        {narrative && (
          <Alert severity="info" icon={<Insights />} sx={{ mt: 2, whiteSpace: 'pre-line' }}>{narrative}</Alert>
        )}
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Bookmark fontSize="small" /> Saved Scenarios
        </Typography>
        {savedScenariosQuery.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={20} /></Box>
        ) : !savedScenariosQuery.data || savedScenariosQuery.data.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No saved scenarios yet — save one above to compare over time.</Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell>Name</TableCell>
                  <TableCell>Scope</TableCell>
                  <TableCell>Saved</TableCell>
                  <TableCell align="right">Projected Profit</TableCell>
                  <TableCell align="right">Projected ROI</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {savedScenariosQuery.data.map((s) => (
                  <TableRow key={s.id} hover>
                    <TableCell>{s.name}</TableCell>
                    <TableCell>
                      {s.farmOperationId || s.projectId ? (
                        <Typography variant="body2" color="primary"
                          sx={{ cursor: 'pointer', display: 'inline-block', '&:hover': { textDecoration: 'underline' } }}
                          onClick={() => s.farmOperationId
                            ? navigate(`/dashboard/farm/${s.farmOperationId}`)
                            : navigate(`/dashboard/projects/${s.projectId}`)}
                        >
                          {s.farmOperationName ?? s.projectName}
                        </Typography>
                      ) : 'Farm-wide'}
                    </TableCell>
                    <TableCell>{new Date(s.createdAt).toLocaleDateString('en-NG')}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color={s.projectedProfit >= 0 ? 'success.main' : 'error.main'}>
                        {fmt(s.projectedProfit)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip label={fmtPct(s.projectedRoiPercent)} size="small" color={s.projectedRoiPercent >= 0 ? 'success' : 'error'} variant="outlined" />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => deleteScenarioMutation.mutate(s.id)} disabled={deleteScenarioMutation.isPending}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Grid>

      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Save Scenario</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth margin="dense" label="Scenario name"
            placeholder='e.g. "Optimistic 2027"'
            value={scenarioName}
            onChange={(e) => setScenarioName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveConfirm(); }}
          />
          {saveMutation.isError && (
            <Alert severity="error" sx={{ mt: 1 }}>Failed to save scenario. Please try again.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveConfirm} disabled={!scenarioName.trim() || saveMutation.isPending}>
            {saveMutation.isPending ? <CircularProgress size={16} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

const ProjectRow = ({ p, navigate, startDate, endDate }: { p: ProjectFinancialSummary; navigate: (path: string) => void; startDate?: string; endDate?: string }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow hover sx={p.overBudget ? { bgcolor: 'error.lighter' } : {}}>
        <TableCell sx={{ pr: 0, width: 40 }}>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell
          sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          onClick={() => navigate(`/dashboard/projects/${p.projectId}`)}
        >
          <Typography variant="body2" fontWeight="medium">{p.projectName}</Typography>
        </TableCell>
        <TableCell align="right"><Typography variant="body2">{fmt(p.budget)}</Typography></TableCell>
        <TableCell align="right">
          <Typography variant="body2" color={p.overBudget ? 'error.main' : 'text.primary'} fontWeight={p.overBudget ? 'bold' : 'normal'}>
            {fmt(p.totalSpent)}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" color={p.remainingBudget < 0 ? 'error.main' : 'text.primary'}>
            {p.remainingBudget < 0 ? `-${fmt(Math.abs(p.remainingBudget))}` : fmt(p.remainingBudget)}
          </Typography>
        </TableCell>
        <TableCell sx={{ minWidth: 180 }}>
          <Tooltip title={`${fmtPct(p.budgetUtilizationPercent)} of budget used`}>
            <Box><UtilizationBar pct={p.budgetUtilizationPercent} overBudget={p.overBudget} /></Box>
          </Tooltip>
        </TableCell>
        <TableCell align="center">
          {p.overBudget ? (
            <Chip icon={<Warning fontSize="small" />} label="Over Budget" color="error" size="small" />
          ) : p.budgetUtilizationPercent > 80 ? (
            <Chip label="Near Limit" color="warning" size="small" />
          ) : (
            <Chip icon={<CheckCircle fontSize="small" />} label="On Track" color="success" size="small" variant="outlined" />
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
          <Collapse in={open} unmountOnExit>
            <ProjectAnalyticsPanel projectId={p.projectId} startDate={startDate} endDate={endDate} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export const ReportsPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [startDate, setStartDate] = useState(toIso(firstOfMonth));
  const [endDate, setEndDate] = useState(toIso(today));
  const [fetchParams, setFetchParams] = useState({ startDate: toIso(firstOfMonth), endDate: toIso(today) });

  // Scope for the analytics tabs (Financial Summary / Crop & Season Performance / Operational) —
  // these are lifetime rollups, not date-ranged, so they're scoped by project/operation/season instead.
  const [scopeProjectId, setScopeProjectId] = useState('');
  const [scopeOperationId, setScopeOperationId] = useState('');
  const [scopeSeasonId, setScopeSeasonId] = useState('');

  const { data: scopeProjects } = useQuery({
    queryKey: ['projects-for-filter'],
    queryFn: () => projectService.getProjects({ size: 100 }),
  });

  const { data: scopeOperations } = useQuery({
    queryKey: ['operations-for-filter', scopeProjectId],
    queryFn: () => farmOperationService.getByProject(scopeProjectId),
    enabled: !!scopeProjectId,
  });

  const { data: scopeSeasons } = useQuery({
    queryKey: ['seasons-for-filter'],
    queryFn: () => seasonService.getAll(),
  });

  const handleScopeProjectChange = (value: string) => {
    setScopeProjectId(value);
    setScopeOperationId('');
  };

  const { data: report, isLoading, isError, error } = useQuery<FinancialReport>({
    queryKey: ['financial-report', fetchParams.startDate, fetchParams.endDate],
    queryFn: () => reportService.getFinancialReport(fetchParams.startDate, fetchParams.endDate),
  });

  const { data: stats } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => dashboardService.getDashboardStats(),
  });

  const applyFilter = () => {
    if (startDate && endDate && startDate <= endDate) {
      setFetchParams({ startDate, endDate });
    }
  };

  const totalOutflow = report?.totalExpenses ?? 0;
  const totalInflow = report?.totalRevenue ?? 0;
  const netBalance = report?.netBalance ?? 0;
  const projects = report?.projectBreakdown ?? [];
  const overBudgetCount = projects.filter((p) => p.overBudget).length;
  const totalBudget = projects.reduce((s, p) => s + p.budget, 0);
  const totalSpent = projects.reduce((s, p) => s + p.totalSpent, 0);
  const projectBudgets = new Map(projects.map((p) => [p.projectId, p.budget]));
  const scopeProjectBudget = scopeProjectId ? projectBudgets.get(scopeProjectId) : undefined;

  const formatDateRange = () => {
    const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };
    const s = new Date(fetchParams.startDate + 'T00:00:00').toLocaleDateString('en-NG', opts);
    const e = new Date(fetchParams.endDate + 'T00:00:00').toLocaleDateString('en-NG', opts);
    return `${s} – ${e}`;
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 0 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>Reports & Analytics</Typography>
        <Typography variant="body2" color="text.secondary">{formatDateRange()}</Typography>
      </Box>

      {/* Date range filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <Typography variant="subtitle2" sx={{ minWidth: 80 }}>Date Range</Typography>
          <TextField
            label="From" type="date" size="small" value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 180 }}
          />
          <TextField
            label="To" type="date" size="small" value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 180 }}
          />
          <Button variant="contained" onClick={applyFilter} disabled={!startDate || !endDate || startDate > endDate}>
            Apply
          </Button>
          <Button variant="outlined" size="small" onClick={() => {
            const s = toIso(firstOfMonth);
            const e = toIso(today);
            setStartDate(s); setEndDate(e);
            setFetchParams({ startDate: s, endDate: e });
          }}>
            This Month
          </Button>
          <Button variant="outlined" size="small" onClick={() => {
            const s = toIso(new Date(today.getFullYear(), 0, 1));
            const e = toIso(today);
            setStartDate(s); setEndDate(e);
            setFetchParams({ startDate: s, endDate: e });
          }}>
            This Year
          </Button>
        </Stack>
      </Paper>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load report: {(error as Error)?.message ?? 'unknown error'}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Overview" />
          <Tab label="Financial Summary" />
          <Tab label="Crop & Season Performance" />
          <Tab label="Operational" />
          <Tab label="Scenario Planner" />
        </Tabs>
      </Paper>

      {tab >= 1 && tab <= 3 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <Typography variant="subtitle2" sx={{ minWidth: 80 }}>Scope</Typography>
            <TextField select size="small" label="Project" value={scopeProjectId}
              onChange={(e) => handleScopeProjectChange(e.target.value)} sx={{ minWidth: 180 }}>
              <MenuItem value="">All Projects (farm-wide)</MenuItem>
              {(scopeProjects?.content ?? []).map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label="Operation" value={scopeOperationId}
              onChange={(e) => setScopeOperationId(e.target.value)} disabled={!scopeProjectId} sx={{ minWidth: 180 }}>
              <MenuItem value="">All Operations</MenuItem>
              {(scopeOperations?.content ?? []).map((op) => (
                <MenuItem key={op.id} value={op.id}>{op.name}</MenuItem>
              ))}
            </TextField>
            {tab === 2 && (
              <TextField select size="small" label="Season" value={scopeSeasonId}
                onChange={(e) => setScopeSeasonId(e.target.value)} sx={{ minWidth: 180 }}>
                <MenuItem value="">All Seasons</MenuItem>
                {(scopeSeasons ?? []).map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name} ({s.year})</MenuItem>
                ))}
              </TextField>
            )}
            {scopeProjectId && (
              <Chip
                label={`Budget: ${scopeProjectBudget != null ? fmt(scopeProjectBudget) : '—'}`}
                color="primary"
                variant="outlined"
              />
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            These are lifetime rollups, not date-ranged — scope by project, operation, or season instead of the date range above.
          </Typography>
        </Paper>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : report ? (
        <>
          {tab === 0 && (
          <>
          {/* Top KPIs */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={6} md={3}>
              <MetricCard title="Total Inflow" value={fmt(totalInflow)} subtitle="harvest revenue" icon={<TrendingUp />} color="success" />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <MetricCard title="Total Outflow" value={fmt(totalOutflow)} subtitle="all payments" icon={<TrendingDown />} color="error" />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <MetricCard
                title="Net Balance"
                value={fmt(netBalance)}
                subtitle={netBalance >= 0 ? 'surplus' : 'deficit'}
                icon={<AccountBalance />}
                color={netBalance >= 0 ? 'primary' : 'warning'}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <MetricCard
                title="Active Projects"
                value={String(stats?.activeProjects ?? 0)}
                subtitle={`${overBudgetCount} over budget`}
                icon={<Agriculture />}
                color={overBudgetCount > 0 ? 'warning' : 'info'}
              />
            </Grid>
          </Grid>

          {/* Executive KPIs — lifetime totals */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Farm Executive Summary (lifetime)</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={6} md={2.4}>
              <MetricCard title="Total Revenue" value={fmt(stats?.totalRevenue ?? 0)} subtitle="lifetime harvest sales" icon={<AttachMoney />} color="success" />
            </Grid>
            <Grid item xs={6} sm={6} md={2.4}>
              <MetricCard title="Total Cost" value={fmt(stats?.totalCost ?? 0)} subtitle="lifetime payments" icon={<TrendingDown />} color="error" />
            </Grid>
            <Grid item xs={6} sm={6} md={2.4}>
              <MetricCard
                title="Net Profit"
                value={fmt(stats?.netProfit ?? 0)}
                subtitle={(stats?.netProfit ?? 0) >= 0 ? 'profitable' : 'loss'}
                icon={<AccountBalance />}
                color={(stats?.netProfit ?? 0) >= 0 ? 'primary' : 'warning'}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={2.4}>
              <MetricCard title="ROI" value={fmtPct(stats?.roiPercent ?? 0)} subtitle="return on total cost" icon={<TrendingUp />} color={(stats?.roiPercent ?? 0) >= 0 ? 'success' : 'error'} />
            </Grid>
            <Grid item xs={6} sm={6} md={2.4}>
              <MetricCard title="Cultivated Acres" value={(stats?.cultivatedAcres ?? 0).toLocaleString()} subtitle="crop operations" icon={<Landscape />} color="info" />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Outflow breakdown */}
            <Grid item xs={12} md={5}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Outflow Breakdown</Typography>
                  <Box sx={{ width: '100%', height: 220 }}>
                    <ResponsiveContainer>
                      <BarChart
                        data={[
                          { label: 'Salary', value: report.salaryPayments },
                          { label: 'Contractor', value: report.contractorFees },
                          { label: 'Other', value: report.otherExpenses },
                        ]}
                        layout="vertical"
                        margin={{ left: 8, right: 24 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e1e0d9" horizontal={false} />
                        <XAxis type="number" tickFormatter={(v) => `₦${(v / 1000).toLocaleString()}k`} tick={{ fontSize: 12 }} />
                        <YAxis type="category" dataKey="label" width={80} tick={{ fontSize: 13 }} />
                        <RechartsTooltip formatter={(v: number) => fmt(v)} />
                        <Bar dataKey="value" fill={CHART_COLORS[0]} radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" fontWeight="bold">Total Outflow</Typography>
                    <Typography variant="body2" fontWeight="bold" color="error.main">{fmt(totalOutflow)}</Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Inflow vs Outflow */}
            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Cash Flow</Typography>
                  <Stack spacing={2} sx={{ mt: 1 }}>
                    <Box>
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <TrendingUp fontSize="small" color="success" />
                          <Typography variant="body2">Inflow</Typography>
                        </Stack>
                        <Typography variant="body2" fontWeight="bold" color="success.main">{fmt(totalInflow)}</Typography>
                      </Stack>
                    </Box>
                    <Box>
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <TrendingDown fontSize="small" color="error" />
                          <Typography variant="body2">Outflow</Typography>
                        </Stack>
                        <Typography variant="body2" fontWeight="bold" color="error.main">{fmt(totalOutflow)}</Typography>
                      </Stack>
                    </Box>
                    <Divider />
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" fontWeight="bold">Net</Typography>
                      <Typography variant="body2" fontWeight="bold" color={netBalance >= 0 ? 'success.main' : 'error.main'}>
                        {netBalance >= 0 ? '+' : ''}{fmt(netBalance)}
                      </Typography>
                    </Stack>
                    <Box sx={{ pt: 1 }}>
                      <Chip label={netBalance >= 0 ? 'Surplus' : 'Deficit'} color={netBalance >= 0 ? 'success' : 'error'} size="small" variant="outlined" />
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Operational stats */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Operations</Typography>
                  <Stack spacing={1.5}>
                    {[
                      { label: 'Active Farm Ops', value: stats?.activeFarmOperations ?? 0 },
                      { label: 'Active Staff', value: stats?.activeStaff ?? 0 },
                      { label: 'Pending Requests', value: stats?.pendingRequests ?? 0 },
                      { label: 'Pending Expenses', value: stats?.pendingExpenses ?? 0 },
                    ].map(({ label, value }) => (
                      <Stack key={label} direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">{label}</Typography>
                        <Chip label={value} size="small" variant="outlined" />
                      </Stack>
                    ))}
                    <Divider />
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">Total Budget Allocated</Typography>
                      <Typography variant="body2" fontWeight="bold">{fmt(totalBudget)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">Total Spent (all projects)</Typography>
                      <Typography variant="body2" fontWeight="bold">{fmt(totalSpent)}</Typography>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Project breakdown — click row to expand individual analytics */}
          <Paper sx={{ p: 0, mb: 3 }}>
            <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" fontWeight="bold">Project Budget Breakdown</Typography>
                <Typography variant="caption" color="text.secondary">Click the arrow to expand individual project analytics</Typography>
              </Box>
              {overBudgetCount > 0 && (
                <Chip icon={<Warning fontSize="small" />} label={`${overBudgetCount} over budget`} color="error" size="small" />
              )}
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ width: 40 }} />
                    <TableCell>Project</TableCell>
                    <TableCell align="right">Budget</TableCell>
                    <TableCell align="right">Spent</TableCell>
                    <TableCell align="right">Remaining</TableCell>
                    <TableCell sx={{ minWidth: 180 }}>Utilization</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {projects.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No projects with budget data
                      </TableCell>
                    </TableRow>
                  ) : (
                    projects
                      .sort((a, b) => (b.overBudget ? 1 : 0) - (a.overBudget ? 1 : 0) || b.budgetUtilizationPercent - a.budgetUtilizationPercent)
                      .map((p: ProjectFinancialSummary) => (
                        <ProjectRow key={p.projectId} p={p} navigate={navigate} startDate={fetchParams.startDate} endDate={fetchParams.endDate} />
                      ))
                  )}
                </TableBody>
                {projects.length > 0 && (
                  <TableBody>
                    <TableRow sx={{ bgcolor: 'action.selected' }}>
                      <TableCell />
                      <TableCell><Typography variant="body2" fontWeight="bold">Total</Typography></TableCell>
                      <TableCell align="right"><Typography variant="body2" fontWeight="bold">{fmt(totalBudget)}</Typography></TableCell>
                      <TableCell align="right"><Typography variant="body2" fontWeight="bold">{fmt(totalSpent)}</Typography></TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold" color={totalBudget - totalSpent < 0 ? 'error.main' : 'text.primary'}>
                          {fmt(Math.max(0, totalBudget - totalSpent))}
                        </Typography>
                      </TableCell>
                      <TableCell colSpan={2} />
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Paper>

          {/* Key Insights */}
          <Paper sx={{ p: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Payments />
              <Typography variant="h6" fontWeight="bold">Key Insights</Typography>
            </Stack>
            <Grid container spacing={2}>
              {[
                {
                  label: 'Cost Recovery Rate',
                  value: totalOutflow > 0 ? fmtPct((totalInflow / totalOutflow) * 100) : '—',
                  note: 'Revenue as % of expenses. Above 100% = profitable.',
                  color: totalInflow > totalOutflow ? 'success.main' : 'warning.main',
                },
                {
                  label: 'Average Spend per Project',
                  value: projects.length > 0 ? fmt(totalSpent / projects.length) : '—',
                  note: `Across ${projects.length} project(s)`,
                  color: 'text.primary',
                },
                {
                  label: 'Budget Remaining (all projects)',
                  value: fmt(Math.max(0, totalBudget - totalSpent)),
                  note: `${totalBudget > 0 ? fmtPct(((totalBudget - totalSpent) / totalBudget) * 100) : '0%'} of total budget`,
                  color: totalBudget - totalSpent < 0 ? 'error.main' : 'text.primary',
                },
                {
                  label: 'Projects Over Budget',
                  value: `${overBudgetCount} / ${projects.length}`,
                  note: overBudgetCount === 0 ? 'All projects within budget' : 'Immediate attention required',
                  color: overBudgetCount > 0 ? 'error.main' : 'success.main',
                },
              ].map(({ label, value, note, color }) => (
                <Grid key={label} item xs={12} sm={6} md={3}>
                  <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography variant="h5" fontWeight="bold" color={color} sx={{ my: 0.5 }}>{value}</Typography>
                    <Typography variant="caption" color="text.secondary">{note}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
          </>
          )}

          {tab === 1 && (
          <>
          {/* Farm Financial Summary — estimated yield/revenue by crop, cost by category, net profit & ROI */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <AttachMoney />
              <Typography variant="h6" fontWeight="bold">Farm Financial Summary</Typography>
            </Stack>
            <FarmFinancialSummarySection navigate={navigate} projectId={scopeProjectId || undefined} operationId={scopeOperationId || undefined} projectBudgets={projectBudgets} />
          </Paper>
          </>
          )}

          {tab === 2 && (
          <>
          {/* Crop Performance — yield variance and profitability by crop variety */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Spa />
              <Typography variant="h6" fontWeight="bold">Crop Performance</Typography>
              <Typography variant="caption" color="text.secondary">— expected vs actual yield, profit by crop</Typography>
            </Stack>
            <CropPerformanceSection projectId={scopeProjectId || undefined} operationId={scopeOperationId || undefined} seasonId={scopeSeasonId || undefined} />
          </Paper>

          {/* Season Comparison — cost/revenue/yield rollups by year-scoped season */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Landscape />
              <Typography variant="h6" fontWeight="bold">Season Comparison</Typography>
              <Typography variant="caption" color="text.secondary">— year over year, e.g. 2026 vs 2027</Typography>
            </Stack>
            <SeasonComparisonSection projectId={scopeProjectId || undefined} operationId={scopeOperationId || undefined} />
          </Paper>
          </>
          )}

          {tab === 3 && (
          <>
          {/* Warnings — low stock inputs and equipment maintenance due */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Warning />
              <Typography variant="h6" fontWeight="bold">Warnings</Typography>
              <Typography variant="caption" color="text.secondary">— low stock &amp; upcoming maintenance</Typography>
            </Stack>
            <WarningsSection navigate={navigate} />
          </Paper>

          {/* Labor Cost — completed task cost split by assignee type, per project */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Groups />
              <Typography variant="h6" fontWeight="bold">Labor Cost by Project</Typography>
              <Typography variant="caption" color="text.secondary">— staff vs contractor vs external, lifetime</Typography>
            </Stack>
            <LaborCostSection navigate={navigate} projectId={scopeProjectId || undefined} />
          </Paper>
          </>
          )}

          {tab === 4 && (
          <>
          {/* Scenario Planner — what-if recompute from the Farm Financial Summary baseline */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Insights />
              <Typography variant="h6" fontWeight="bold">Scenario Planner</Typography>
              <Typography variant="caption" color="text.secondary">— what-if yield, price &amp; cost changes</Typography>
            </Stack>
            <ScenarioPlannerSection />
          </Paper>
          </>
          )}
        </>
      ) : null}
    </Box>
  );
};
